import type { Project } from '../types';
import { createGraph, playVoice, syncGraph } from './graph';
import {
  eventsForRange,
  loopLength,
  patternModeBeats,
  songBeats,
  trackAudible,
  type PlayMode,
} from './sequencer';

/**
 * Offline bounce.
 *
 * Reuses the live sequencer and mixer graph inside an OfflineAudioContext, so
 * an export sounds like what you heard — no second code path to drift.
 */

export interface RenderOptions {
  mode: PlayMode;
  /** Repeats of the loop (pattern mode). Song mode always renders once. */
  loops: number;
  /** Extra seconds captured after the last event so tails aren't clipped. */
  tail: number;
  sampleRate?: number;
  /** Restrict to these track ids (stem rendering). */
  onlyTracks?: string[];
  /** Deterministic probability rolls. */
  seed?: number;
}

const latencyCache = new Map<number, number>();

/**
 * Samples of delay the master chain adds.
 *
 * `DynamicsCompressorNode` has an internal lookahead — about 6 ms in Chrome —
 * and it is not exposed anywhere in the API. Left uncompensated, every bounce
 * and every export comes out that much late, with the tail clipped by the same
 * amount; bounce a track twice and it has drifted 12 ms off its own grid.
 *
 * So measure it: push an impulse through an identical graph and see where it
 * lands. Cheap, cached, and correct on whatever engine the browser ships.
 */
async function measureGraphLatency(sampleRate: number): Promise<number> {
  const cached = latencyCache.get(sampleRate);
  if (cached !== undefined) return cached;

  const frames = Math.ceil(0.2 * sampleRate);
  const ctx = new OfflineAudioContext(2, frames, sampleRate);
  const graph = createGraph(ctx, ctx.destination);
  const impulse = ctx.createBuffer(1, 1, sampleRate);
  impulse.getChannelData(0)[0] = 1;
  const src = ctx.createBufferSource();
  src.buffer = impulse;
  src.connect(graph.master);
  src.start(0);

  const out = await ctx.startRendering();
  const data = out.getChannelData(0);
  let peak = 0;
  let at = 0;
  for (let i = 0; i < data.length; i++) {
    const v = Math.abs(data[i]);
    if (v > peak) {
      peak = v;
      at = i;
    }
  }
  const latency = peak > 1e-6 ? at : 0;
  latencyCache.set(sampleRate, latency);
  return latency;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function renderLengthBeats(project: Project, opts: RenderOptions): number {
  if (opts.mode === 'song') return songBeats(project) || patternModeBeats(project);
  return loopLength(project, 'pattern') * Math.max(1, opts.loops);
}

export async function renderProject(
  project: Project,
  opts: RenderOptions,
): Promise<AudioBuffer> {
  const spb = 60 / project.bpm;
  const beats = renderLengthBeats(project, opts);
  const sampleRate = opts.sampleRate ?? 44100;
  const seconds = beats * spb + opts.tail;
  const frames = Math.max(1, Math.ceil(seconds * sampleRate));

  const scoped: Project = opts.onlyTracks
    ? {
        ...project,
        tracks: project.tracks.map((t) =>
          opts.onlyTracks!.includes(t.id)
            ? { ...t, mute: false, solo: false }
            : { ...t, mute: true, solo: false },
        ),
      }
    : project;

  // Render past the end by the graph's own latency, then drop that much off
  // the front, so bar 1 lands on sample 0.
  const latency = await measureGraphLatency(sampleRate);
  const ctx = new OfflineAudioContext(2, frames + latency, sampleRate);
  const graph = createGraph(ctx, ctx.destination);
  syncGraph(graph, scoped, (t) => trackAudible(scoped, t));

  const rng = mulberry32(opts.seed ?? 0x5eed);
  const loopBeats = loopLength(scoped, opts.mode);

  // Schedule the entire timeline up front, looping the pattern as needed.
  let cursor = 0;
  while (cursor < beats) {
    const cycleStart = Math.floor(cursor / loopBeats) * loopBeats;
    const localTo = Math.min(beats - cycleStart, loopBeats);
    const events = eventsForRange(scoped, {
      mode: opts.mode,
      from: cursor - cycleStart,
      to: localTo,
      timeAtZero: cycleStart * spb,
      rng,
    });
    for (const e of events) playVoice(graph, e);
    cursor = cycleStart + localTo;
    if (localTo <= 0) break;
  }

  const rendered = await ctx.startRendering();
  if (latency <= 0) return rendered;

  const trimmed = new OfflineAudioContext(2, frames, sampleRate).createBuffer(
    rendered.numberOfChannels,
    frames,
    sampleRate,
  );
  for (let c = 0; c < rendered.numberOfChannels; c++) {
    trimmed.copyToChannel(rendered.getChannelData(c).subarray(latency, latency + frames), c);
  }
  return trimmed;
}

/** Renders one track's pattern as a fresh loop, ready to re-import as a source. */
export async function bounceTrack(
  project: Project,
  trackId: string,
  loops = 1,
): Promise<AudioBuffer> {
  return renderProject(project, {
    mode: 'pattern',
    loops,
    tail: 0.25,
    onlyTracks: [trackId],
  });
}
